import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { TalentSemanticsFeedbackStatus } from '../../../../../../../shared/common/talentSemanticsTypes';
import {
  TSpellAggregateArgs,
  TSpellAggregateResponse,
  TSpellOverrideArgs,
  TSpellOverrideResponse,
  TTalentFeedbackAggregateArgs,
  TTalentFeedbackAggregateResponse,
  TTalentFeedbackBatchUpdateArgs,
  TTalentFeedbackBatchUpdateResponse,
  ITalentFeedbackListRequest,
  TTalentFeedbackListResponse,
  TTalentFeedbackRow,
  TTalentFeedbackUpdateArgs,
  TTalentOverride,
  TUpsertOverrideArgs,
  TDeleteOverrideArgs,
  TToggleOverrideEnabledArgs,
  TApplyOverrideToSpecsArgs,
  TApplyOverrideToSpecsResponse,
  TSpellSpecsArgs,
  TSpellSpecsResponse
} from '../../../../../../../shared/types/talentSemanticsFeedbackAdminTypes';
import { networkBaseQuery } from '../../../services/api/utils';

const SEMANTICS_BASE = '/admin/api/talents/semantics';
const BASE = `${SEMANTICS_BASE}/feedback`;

// A single LIST tag per resource. The queries provide it; mutations patch the
// mounted list optimistically (see `patchLists`) instead of invalidating it, so a
// status change never triggers a refetch/spinner. AGGREGATE is still tag-driven —
// it refetches silently and doesn't gate the table.
const LIST_TAG = { type: 'TalentFeedbackList', id: 'LIST' } as const;
const AGGREGATE_TAG = { type: 'TalentFeedbackAggregate', id: 'LIST' } as const;
const SPELLS_TAG = { type: 'TalentFeedbackSpells', id: 'LIST' } as const;
const OVERRIDE_TAG = 'TalentFeedbackOverride' as const;
// Per-spell-and-build: the carrying specs are fixed by the game data, but each
// entry's override state flips whenever an override is written for that spell.
const SPELL_SPECS_TAG = 'TalentFeedbackSpellSpecs' as const;

// Fold a status change into a single cached list page. When an active status
// filter no longer matches the new status the row is removed (so it leaves the
// page exactly as a refetch would); otherwise the status flips in place.
function applyStatusToDraft(
  draft: TTalentFeedbackListResponse,
  args: ITalentFeedbackListRequest,
  ids: number[],
  status: TalentSemanticsFeedbackStatus
) {
  const activeFilter = args.status;
  const dropsRow = Boolean(activeFilter?.length) && !activeFilter!.includes(status);
  ids.forEach((id) => {
    const idx = draft.data.findIndex((row) => row.id === id);
    if (idx === -1) {
      return;
    }
    if (dropsRow) {
      draft.data.splice(idx, 1);
      draft.meta.total = Math.max(0, draft.meta.total - 1);
    } else {
      draft.data[idx] = { ...draft.data[idx], status };
    }
  });
}

type OptimisticApi = { dispatch: ThunkDispatch<unknown, unknown, UnknownAction>; getState: () => unknown };

// Multi-value filters are sent as one comma-separated param (status=new,accepted) —
// see the getTalentFeedbackList query comment. Empty arrays are dropped entirely.
function joinFilterValues(values: string[] | undefined): string | undefined {
  return values?.length ? values.join(',') : undefined;
}

// Patch every cached list page that provides LIST_TAG and return the undo handles
// so a rejected/partly-failed mutation can roll the guess back.
function patchLists(api: OptimisticApi, ids: number[], status: TalentSemanticsFeedbackStatus) {
  const state = api.getState() as Parameters<typeof talentSemanticsFeedbackAdminApi.util.selectInvalidatedBy>[0];
  return talentSemanticsFeedbackAdminApi.util
    .selectInvalidatedBy(state, [LIST_TAG])
    .filter((entry) => entry.endpointName === 'getTalentFeedbackList')
    .map((entry) => {
      const args = entry.originalArgs as ITalentFeedbackListRequest;
      return api.dispatch(
        talentSemanticsFeedbackAdminApi.util.updateQueryData('getTalentFeedbackList', args, (draft) =>
          applyStatusToDraft(draft, args, ids, status)
        )
      );
    });
}

export const talentSemanticsFeedbackAdminApi = createApi({
  reducerPath: 'talentSemanticsFeedbackAdminApi',
  baseQuery: networkBaseQuery,
  tagTypes: [
    'TalentFeedbackList',
    'TalentFeedbackAggregate',
    'TalentFeedbackSpells',
    'TalentFeedbackOverride',
    'TalentFeedbackSpellSpecs'
  ],
  endpoints: (builder) => ({
    getTalentFeedbackList: builder.query<TTalentFeedbackListResponse, ITalentFeedbackListRequest>({
      // Query params travel in camelCase, matching the backend filter shape;
      // `undefined` keys are dropped by qs.stringify. Multi-value filters travel
      // as ONE comma-separated param (status=new,accepted): Hapi's query parser
      // doesn't expand bracket-indexed arrays, and the route's Joi validator
      // splits the string back into string[].
      query: (args) => ({
        url: BASE,
        params: {
          status: joinFilterValues(args.status),
          issueType: joinFilterValues(args.issueType),
          spellId: args.spellId,
          specId: args.specId,
          dbcIndexVersion: args.dbcIndexVersion,
          referenceCode: args.referenceCode,
          page: args.page,
          limit: args.limit
        }
      }),
      providesTags: [LIST_TAG]
    }),
    getTalentFeedbackAggregate: builder.query<TTalentFeedbackAggregateResponse, TTalentFeedbackAggregateArgs>({
      query: (args) => ({ url: `${BASE}/aggregate`, params: { status: joinFilterValues(args.status) } }),
      providesTags: [AGGREGATE_TAG]
    }),
    updateTalentFeedback: builder.mutation<TTalentFeedbackRow, TTalentFeedbackUpdateArgs>({
      query: ({ id, status, reviewerNotes }) => ({
        url: `${BASE}/${id}`,
        method: 'PATCH',
        body: {
          ...(status && { status }),
          ...(reviewerNotes !== undefined && { reviewer_notes: reviewerNotes })
        }
      }),
      // Optimistic status flip — no LIST invalidation, so the table never spins.
      async onQueryStarted({ id, status }, { dispatch, getState, queryFulfilled }) {
        if (!status) {
          return;
        }
        const patches = patchLists({ dispatch, getState }, [id], status);
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [AGGREGATE_TAG]
    }),
    batchUpdateTalentFeedback: builder.mutation<TTalentFeedbackBatchUpdateResponse, TTalentFeedbackBatchUpdateArgs>({
      query: ({ ids, status, reviewerNotes }) => ({
        url: `${BASE}/batch`,
        method: 'PATCH',
        body: {
          ids,
          ...(status && { status }),
          ...(reviewerNotes !== undefined && { reviewer_notes: reviewerNotes })
        }
      }),
      // Same optimistic flip. On a partial failure the batch endpoint returns 200
      // with `failed[]`; undo the guess and resync that one case from the server.
      async onQueryStarted({ ids, status }, { dispatch, getState, queryFulfilled }) {
        if (!status) {
          return;
        }
        const patches = patchLists({ dispatch, getState }, ids, status);
        try {
          const { data } = await queryFulfilled;
          if (data?.failed?.length) {
            patches.forEach((patch) => patch.undo());
            dispatch(talentSemanticsFeedbackAdminApi.util.invalidateTags([LIST_TAG]));
          }
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [AGGREGATE_TAG]
    }),
    getTalentFeedbackSpells: builder.query<TSpellAggregateResponse, TSpellAggregateArgs>({
      query: (args) => ({
        url: `${SEMANTICS_BASE}/spells`,
        params: {
          specId: args.specId,
          dbcIndexVersion: args.dbcIndexVersion,
          // Flag defaults live in the route schema; only deviations travel.
          ...(args.hasFeedback === false ? { hasFeedback: 'false' } : {}),
          ...(args.hasOverride ? { hasOverride: 'true' } : {}),
          ...(args.needsReview ? { needsReview: 'true' } : {})
        }
      }),
      providesTags: [SPELLS_TAG]
    }),
    getSpellOverride: builder.query<TSpellOverrideResponse, TSpellOverrideArgs>({
      query: (args) => ({
        url: `${SEMANTICS_BASE}/override`,
        params: { spellId: args.spellId, specId: args.specId, dbcIndexVersion: args.dbcIndexVersion }
      }),
      // Overrides are keyed per DBC version on the backend; mirror that in the cache key.
      providesTags: (_res, _err, args) => [
        { type: OVERRIDE_TAG, id: `${args.spellId}:${args.specId}:${args.dbcIndexVersion}` }
      ]
    }),
    getSpellSpecs: builder.query<TSpellSpecsResponse, TSpellSpecsArgs>({
      query: (args) => ({
        url: `${SEMANTICS_BASE}/spell/specs`,
        params: { spellId: args.spellId, specId: args.specId, dbcIndexVersion: args.dbcIndexVersion }
      }),
      // Keyed on spell + build only: every spec of the set answers with the same list,
      // so switching specs reuses the cached entry instead of refetching.
      providesTags: (_res, _err, args) => [{ type: SPELL_SPECS_TAG, id: `${args.spellId}:${args.dbcIndexVersion}` }]
    }),
    applyOverrideToSpecs: builder.mutation<TApplyOverrideToSpecsResponse, TApplyOverrideToSpecsArgs>({
      query: (args) => ({
        url: `${SEMANTICS_BASE}/override/apply-to-specs`,
        method: 'POST',
        body: {
          spellId: args.spellId,
          specId: args.specId,
          targetSpecIds: args.targetSpecIds,
          dbcIndexVersion: args.dbcIndexVersion
        }
      }),
      // Each target spec now has its own override row, so drop every target's cached
      // override alongside the spec list (whose `hasOverride` flags just changed).
      invalidatesTags: (_res, _err, args) => [
        ...args.targetSpecIds.map((targetSpecId) => ({
          type: OVERRIDE_TAG,
          id: `${args.spellId}:${targetSpecId}:${args.dbcIndexVersion}`
        })),
        { type: SPELL_SPECS_TAG, id: `${args.spellId}:${args.dbcIndexVersion}` },
        SPELLS_TAG
      ]
    }),
    upsertSpellOverride: builder.mutation<TTalentOverride, TUpsertOverrideArgs>({
      query: (args) => ({
        url: `${SEMANTICS_BASE}/override`,
        method: 'PUT',
        body: {
          spellId: args.spellId,
          specId: args.specId ?? null,
          dbcIndexVersion: args.dbcIndexVersion,
          categories: args.categories,
          ...(args.primaryCategory && { primaryCategory: args.primaryCategory }),
          ...(args.confidence && { confidence: args.confidence }),
          ...(args.notes !== undefined && { notes: args.notes })
        }
      }),
      // The override drives both the per-spell view and the Spells-tab rollup columns.
      invalidatesTags: (_res, _err, args) => [
        { type: OVERRIDE_TAG, id: `${args.spellId}:${args.specId ?? 'all'}:${args.dbcIndexVersion}` },
        { type: SPELL_SPECS_TAG, id: `${args.spellId}:${args.dbcIndexVersion}` },
        SPELLS_TAG
      ]
    }),
    deleteSpellOverride: builder.mutation<void, TDeleteOverrideArgs>({
      query: (args) => ({
        url: `${SEMANTICS_BASE}/override`,
        method: 'DELETE',
        params: {
          spellId: args.spellId,
          specId: args.specId ?? null,
          dbcIndexVersion: args.dbcIndexVersion
        }
      }),
      invalidatesTags: (_res, _err, args) => [
        { type: OVERRIDE_TAG, id: `${args.spellId}:${args.specId ?? 'all'}:${args.dbcIndexVersion}` },
        { type: SPELL_SPECS_TAG, id: `${args.spellId}:${args.dbcIndexVersion}` },
        SPELLS_TAG
      ]
    }),
    toggleSpellOverrideEnabled: builder.mutation<TTalentOverride, TToggleOverrideEnabledArgs>({
      query: (args) => ({
        url: `${SEMANTICS_BASE}/override/enabled`,
        method: 'PATCH',
        body: {
          spellId: args.spellId,
          specId: args.specId ?? null,
          dbcIndexVersion: args.dbcIndexVersion,
          enabled: args.enabled
        }
      }),
      invalidatesTags: (_res, _err, args) => [
        { type: OVERRIDE_TAG, id: `${args.spellId}:${args.specId ?? 'all'}:${args.dbcIndexVersion}` },
        { type: SPELL_SPECS_TAG, id: `${args.spellId}:${args.dbcIndexVersion}` },
        SPELLS_TAG
      ]
    })
  })
});

export const {
  useGetTalentFeedbackListQuery,
  useGetTalentFeedbackAggregateQuery,
  useUpdateTalentFeedbackMutation,
  useBatchUpdateTalentFeedbackMutation,
  useGetTalentFeedbackSpellsQuery,
  useGetSpellOverrideQuery,
  useGetSpellSpecsQuery,
  useUpsertSpellOverrideMutation,
  useApplyOverrideToSpecsMutation,
  useDeleteSpellOverrideMutation,
  useToggleSpellOverrideEnabledMutation
} = talentSemanticsFeedbackAdminApi;
